import React, { useCallback, useEffect, useState } from 'react';
import { Cart } from 'types/cart';
import { sdk } from 'sdk';
import { Category } from 'types/category';
import { Country } from 'types/country';
import { CustomerGroup } from 'types/customerGroup';
import { Account } from '@loomstateorg/shared-frontend/types/account';
import { Result } from '@commercetools/frontend-domain-types/product';
import type { AllowedCountriesCodesPayload } from 'frontastic/hooks/useGeoData/type';

const emptyAllowedCountryCodes: AllowedCountriesCodesPayload = {
  allowedCountryCodes: [],
};

const GlobalContext = React.createContext({
  cart: {} as unknown as Cart,
  allCategories: {} as unknown as Category[],
  allCustomerGroups: {} as unknown as CustomerGroup[],
  countries: {} as any[],
  allowedCountryCodes: [] as string[],
  showOnlyAllowedCountries: false,
  currentProductCategory: {} as unknown as Category,
  setCart: (cart: Cart) => {},
  getCart: () => {},
  setAllCategories: (allCategories: Category[]) => {},
  getAllCategories: () => {},
  getAllCustomerGroups: () => {},
  setCountries: (countries: any[]) => {},
  getCountries: () => {},
  getAllowedCountries: () => Promise.resolve(emptyAllowedCountryCodes),
  syncCountryFilterFromAccount: (_account?: Account | null) => {},
  setCurrentProductCategory: (category: Category) => {},
  excludedCategoryKeys: [] as string[],
  hideTax: false,
  setHideTax: (hideTax: boolean) => {},
  getHideTax: () => {},
});

const GlobalCTProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const extensions = sdk.composableCommerce;
  const [cart, setCart] = useState<Cart>({} as unknown as Cart);
  const [allCategories, setAllCategories] = useState<Category[]>({} as unknown as Category[]);
  const [allGeoCountries, setAllGeoCountries] = useState<Country[]>([]);
  const [countries, setCountries] = useState<Country[]>([] as unknown as Country[]);
  const [allowedCountryCodes, setAllowedCountryCodes] = useState<string[]>([]);
  const [showOnlyAllowedCountries, setShowOnlyAllowedCountries] = useState(false);
  const [allCustomerGroups, setAllCustomerGroups] = useState<CustomerGroup[]>({} as unknown as CustomerGroup[]);
  const [currentProductCategory, setCurrentProductCategory] = useState<Category>({} as unknown as Category);
  const [excludedCategoryKeys, setExcludedCategoryKeys] = useState<string[]>([]);
  const [hideTax, setHideTax] = useState<boolean>(false);

  const getCart = useCallback(async () => {
    const res = await extensions.cart.getCart();
    const current = (res.isError ? {} : res.data) as Cart;
    setCart(current);
  }, []);

  const getAllCategories = useCallback(async () => {
    const res = await sdk.callAction({ actionName: 'product/getAllCategories', query: { limit: 99 } });
    const cats = res.isError ? [] : ((res?.data as Result).items as Category[]);
    setAllCategories(cats);
  }, []);

  const syncCountryFilterFromAccount = useCallback((account?: Account | null) => {
    setShowOnlyAllowedCountries(account?.showOnlyAllowedCountries === true);
  }, []);

  const getAllowedCountries = useCallback(async () => {
    const res = await sdk.callAction<AllowedCountriesCodesPayload>({ actionName: 'account/getAllowedCountries' });
    const payload = res.isError ? emptyAllowedCountryCodes : (res.data ?? emptyAllowedCountryCodes);
    setAllowedCountryCodes(payload.allowedCountryCodes);
    return payload;
  }, []);

  const getCountries = useCallback(async () => {
    const [geoRes, allowedRes] = await Promise.all([
      sdk.callAction({ actionName: 'geodata/getCountries' }),
      sdk.callAction<AllowedCountriesCodesPayload>({ actionName: 'account/getAllowedCountries' }),
    ]);

    const allCountries = geoRes.isError ? [] : ((geoRes?.data as Result as unknown as Country[]) ?? []);
    const allowedPayload = allowedRes.isError
      ? emptyAllowedCountryCodes
      : (allowedRes.data ?? emptyAllowedCountryCodes);

    setAllGeoCountries(allCountries);
    setAllowedCountryCodes(allowedPayload.allowedCountryCodes);
  }, []);

  useEffect(() => {
    if (!allGeoCountries.length) {
      setCountries([]);
      return;
    }
    const next = showOnlyAllowedCountries
      ? allGeoCountries.filter((c) => allowedCountryCodes.includes(c.code))
      : allGeoCountries;
    const sorted = [...next].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    setCountries(sorted);
  }, [allGeoCountries, allowedCountryCodes, showOnlyAllowedCountries]);

  const getAllCustomerGroups = useCallback(async () => {
    const res = await sdk.callAction({ actionName: 'account/getAllCustomerGroups' });
    const customerGroups = res.isError ? [] : (res?.data as Result as unknown as CustomerGroup[]);
    setAllCustomerGroups(customerGroups);
  }, []);

  const getHideTax = useCallback(async () => {
    const res = await sdk.callAction({ actionName: 'user-config/getUserConfig' });
    const value = res.isError ? false : (res?.data as { hideTax: boolean })?.hideTax ?? false;
    setHideTax(value);
  }, []);

  useEffect(() => {
    getHideTax();
  }, [getHideTax]);

  useEffect(() => {
    (async () => {
      const response = await sdk.callAction({ actionName: 'product/getExcludedCategoryKeys' });
      let data = [];
      if (!response.isError && response.data) {
        try {
          const parsed = JSON.parse(response.data as string);
          // Ensure we always get an array, handle cases where response might be "" or {}
          data = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          data = [];
        }
      }
      setExcludedCategoryKeys(data);
    })();
  }, []);

  return (
    <GlobalContext.Provider
      value={{
        cart,
        setCart,
        getCart,
        allCategories,
        setAllCategories,
        getAllCategories,
        countries,
        allowedCountryCodes,
        showOnlyAllowedCountries,
        setCountries,
        getCountries,
        getAllowedCountries,
        syncCountryFilterFromAccount,
        allCustomerGroups,
        getAllCustomerGroups,
        currentProductCategory,
        setCurrentProductCategory,
        excludedCategoryKeys,
        hideTax,
        setHideTax,
        getHideTax,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export { GlobalContext, GlobalCTProvider };
